/**
 * Everything that crosses the socket, and the only place that says what it
 * looks like.
 *
 * Two rules, both borrowed wholesale from BreachProtocol's protocol.ts because
 * they earned their keep there:
 *
 *   1. TUPLES FOR HOT PATHS, OBJECTS FOR EVERYTHING ELSE.  A snapshot goes out
 *      twenty times a second to every client and carries a row per cat; spelt
 *      as `{ "slot": 3, "x": 1.2, ... }` most of the bytes would be key names.
 *      Handshakes, claims and blisses happen a handful of times a session and
 *      are objects, because there the readable thing is worth more than the
 *      short thing.  Every tuple here has named column constants -- the
 *      ENCODING is packed, the code reading it is not.
 *
 *   2. CLIENT INPUT IS UNTRUSTED.  Anything in a client message is a claim.
 *      The parsers at the bottom are total functions from `unknown`: they never
 *      throw, and a malformed message yields null rather than a half-populated
 *      object a caller might use anyway.  The server re-derives every
 *      consequence that matters -- which cat you own, whether you were really
 *      standing in the creek, what your happiness is -- from state it holds
 *      itself.
 *
 * The one asymmetry: parseServerMessage is a shape check and a cast.  The
 * client trusts its own server, because the server is what served it the page.
 */
import { PROTO_VERSION, PROTO_MIN, STATES, NAME_MAX, PREY_SPECIES, PREY_MODES, } from "./constants.js";
import { PHRASE_COUNT } from "./phrases.js";
// PROTO_VERSION alone is re-exported here (and re-resolved in index.ts --
// its header explains the star-export collision).  PROTO_MIN is NOT: one
// name paying that toll is a documented wrinkle, two is a habit, and the
// package root already carries it via the constants star.
export { PROTO_VERSION };
// ---------------------------------------------------------------------------
// Rounding.  Positions to the centimetre, angles to about a twentieth of a
// degree.  A cat is 0.32 m at the shoulder and rendered at a distance where a
// centimetre is well under a pixel, so everything past these digits is float64
// printing its own noise into a packet twenty times a second.  Measured on a
// full sixteen-cat snapshot: 1140 bytes down to 700.
// ---------------------------------------------------------------------------
export const round2 = (v) => Math.round(v * 100) / 100;
export const round3 = (v) => Math.round(v * 1000) / 1000;
// ---------------------------------------------------------------------------
// The snapshot row.  One cat, as it is right now.
//
// `slot` and not the cat's persistent id: an id is a 36-character string that
// would be two thirds of the row, and the roster is at most sixteen cats, so a
// small integer stable for the life of the process is the honest wire
// identity.  The `cat` message is what ties a slot to a record.
// ---------------------------------------------------------------------------
export const CatCol = {
    SLOT: 0, X: 1, Y: 2, Z: 3, FACING: 4, STATE: 5, CHARGE: 6, HAPPY: 7, FLAGS: 8,
};
export const CAT_ROW_LEN = 9;
/** Pack a cat into a snapshot row, rounding on the way out. */
export function catRow(slot, { x, y, z, facing, state, charge, happy, flags }) {
    return [
        slot,
        round2(x), round2(y), round2(z),
        round3(facing),
        STATES.indexOf(state) < 0 ? 0 : STATES.indexOf(state),
        round2(charge),
        round2(happy),
        flags,
    ];
}
// ---------------------------------------------------------------------------
// The pose frame, client to server.  Same shape as a row minus the fields the
// server owns: a client does not get to tell the server how happy its cat is.
// ---------------------------------------------------------------------------
export const PoseCol = { X: 0, Y: 1, Z: 2, FACING: 3, STATE: 4, CHARGE: 5 };
export const POSE_LEN = 6;
export function poseFrame({ x, y, z, facing, state, charge }) {
    const si = STATES.indexOf(state);
    return [round2(x), round2(y), round2(z), round3(facing), si < 0 ? 0 : si, round2(charge)];
}
// ---------------------------------------------------------------------------
// The prey row.  One mouse or bird, as the server's simulation holds it.
//
// A tuple for the same reason the cat row is one: it rides the snapshot
// cadence to every client, so the bytes are mostly numbers, not key names.
// Species and mode travel as indexes into the append-only arrays in
// constants.js, exactly the STATES bargain.  CARRIER is a cat slot, or -1:
// a carried prey is drawn in that cat's mouth on every screen, which is how
// the whole street sees who is bringing a gift home.
//
// There is deliberately NO client->server message anywhere in this file for
// the hunt.  A capture is decided by the server from the pose it already
// accepted and the prey it already simulates; a delivery from the same.  A
// client that could say "I caught it" would be a client that can mint
// happiness, and rule 2 at the top of this file is the whole reason it
// cannot.
// ---------------------------------------------------------------------------
export const PreyCol = {
    ID: 0, SPECIES: 1, MODE: 2, X: 3, Y: 4, Z: 5, HEADING: 6, CARRIER: 7,
};
export const PREY_ROW_LEN = 8;
export function preyRow({ id, species, mode, x, y, z, heading, carrier }) {
    const si = PREY_SPECIES.indexOf(species);
    const mi = PREY_MODES.indexOf(mode);
    return [
        id,
        si < 0 ? 0 : si,
        mi < 0 ? 0 : mi,
        round2(x), round2(y), round2(z),
        round3(heading),
        Number.isInteger(carrier) ? carrier : -1,
    ];
}
// ---------------------------------------------------------------------------
// Reasons a join is refused.  Enumerated so the client can say something
// useful; a refusal the player cannot act on is indistinguishable from a bug.
// ---------------------------------------------------------------------------
export const DENY = {
    FULL: "full", // sixteen cats already live here and none is yours
    STALE: "stale", // the token you offered names a cat that is gone
    BUSY: "busy", // that cat is already being played, in another tab
    VERSION: "version", // your dialect is outside [PROTO_MIN, PROTO_VERSION];
    // this deny carries the window as `min`/`max`, and
    // CLOSE.PROTO follows it (constants.ts §4.3 block)
};
// ---------------------------------------------------------------------------
// Total parse helpers.  Each is a function from `unknown`.
// ---------------------------------------------------------------------------
function isRecord(v) {
    return typeof v === "object" && v !== null && !Array.isArray(v);
}
/** A finite number, or `fallback`.  Rejects NaN, Infinity, strings, null --
    every one of which JSON can carry and arithmetic will silently swallow. */
export function num(v, fallback = 0) {
    return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}
/** A finite number in [lo, hi], or `fallback`.  Clamped rather than refused:
    the bounds here are shared constants, so a value outside them is a stale
    client rather than an attack, and the nearest legal value is a better
    answer than dropping the whole frame. */
export function clampNum(v, lo, hi, fallback = lo) {
    const n = typeof v === "number" && Number.isFinite(v) ? v : fallback;
    return Math.min(hi, Math.max(lo, n));
}
/** A string of at most `max` characters, or null. */
export function str(v, max) {
    return typeof v === "string" && v.length <= max ? v : null;
}
/**
 * A pose frame, or null.
 *
 * Length is checked exactly and every slot has to be a finite number.  A frame
 * with a NaN in it is worse than a missing frame: the position is written
 * straight into the world and a NaN propagates into every distance test that
 * touches it for the rest of the process's life.
 */
export function readPose(v) {
    if (!Array.isArray(v) || v.length !== POSE_LEN)
        return null;
    for (let i = 0; i < POSE_LEN; i++) {
        if (typeof v[i] !== "number" || !Number.isFinite(v[i]))
            return null;
    }
    const si = v[PoseCol.STATE];
    return {
        x: v[PoseCol.X], y: v[PoseCol.Y], z: v[PoseCol.Z],
        facing: v[PoseCol.FACING],
        // Out-of-range state indexes read as idle rather than refusing the frame:
        // the position in it is still good, and STATES only ever grows, so this is
        // what a client one deploy behind looks like.
        state: STATES[si] || STATES[0],
        charge: clampNum(v[PoseCol.CHARGE], 0, 1, 0),
    };
}
/**
 * Parse one frame from a client.  Never throws; null means "drop this".
 *
 * The `look` is deliberately NOT validated here.  It belongs to the
 * customizer's schema and normalize() there is already the total function this
 * file would otherwise duplicate -- so the raw value is carried through and the
 * caller runs it through normalizeRecord.  One validator per shape, and the
 * one that exists wins.
 */
export function parseClientMessage(raw) {
    let msg;
    try {
        msg = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!isRecord(msg) || typeof msg.t !== "string")
        return null;
    switch (msg.t) {
        case "meow":
            return { t: "meow" };
        case "join":
            return {
                t: "join",
                // A missing name is legal -- the server names an unnamed cat -- but a
                // name of the wrong type is a client bug and reads as missing.
                name: str(msg.name, NAME_MAX * 4) ?? "",
                look: isRecord(msg.look) ? msg.look : null,
                // 64 hex characters at most; anything longer is not a token this
                // server ever minted, so it can be refused before it is compared.
                token: str(msg.token, 128),
                // The dialect the client says it speaks, judged against
                // [PROTO_MIN, PROTO_VERSION] in world.claim.  A claim like any
                // other, so garbage does not refuse the frame: anything that is
                // not a positive integer reads as null, which the server treats
                // as v1 -- clients minted before negotiation existed never say
                // their version, and every one of them speaks v1 (constants.ts).
                v: typeof msg.v === "number" && Number.isInteger(msg.v) && msg.v >= 1 ? msg.v : null,
            };
        case "state": {
            const pose = readPose(msg.s);
            return pose ? { t: "state", pose } : null;
        }
        case "look":
            return isRecord(msg.look) ? { t: "look", look: msg.look } : null;
        case "name": {
            const name = str(msg.name, NAME_MAX * 4);
            return name === null ? null : { t: "name", name };
        }
        case "ping":
            // The stamp is echoed back untouched and never used in arithmetic here,
            // so it only has to be a number at all.
            return { t: "ping", c: num(msg.c, 0) };
        case "say": {
            // Quick-chat.  The one thing that may cross the wire is an INDEX into
            // shared/phrases.js -- never text; the table itself is the safety
            // model and its header carries the whole argument.  So the check is
            // exact and unforgiving where clampNum would usually be neighbourly:
            // an out-of-range slider is a stale client best served by the nearest
            // legal value, but a phrase index has no "nearest" -- clamping 1e9
            // to the last phrase would put a specific sentence in a cat's mouth
            // that nobody chose.  A fraction, a string, a negative, or an index
            // past the table therefore reads as silence.  (An index past OUR
            // table is also what a client one deploy AHEAD looks like, phrases.js
            // being append-only -- dropping it degrades that skew to a phrase not
            // shown, which is why this message did not bump PROTO_VERSION.)
            const p = msg.p;
            return typeof p === "number" && Number.isInteger(p) && p >= 0 && p < PHRASE_COUNT
                ? { t: "say", p }
                : null;
        }
        default:
            return null;
    }
}
/**
 * The shape check on the way in to the browser.
 *
 * Not a validator: the client trusts this server, because this server is what
 * served it.  What it does buy is that a truncated frame or a proxy that
 * injected something is a dropped message rather than an exception inside a
 * requestAnimationFrame callback, where it would take the render loop with it.
 */
export function parseServerMessage(raw) {
    let msg;
    try {
        msg = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!isRecord(msg) || typeof msg.t !== "string")
        return null;
    return msg;
}
// ---- constructors, so no send site spells a message by hand ---------------
/** `proto` is the server's newest dialect (kept for clients that predate
    the window and still compare it strictly); `win` is the whole window, so
    a client can judge ITSELF in rather than demand equality -- the check in
    net/index.js.  Added beside `proto`, never instead of it: fields are
    append-only, and the old strict comparison is what every already-shipped
    tab still runs. */
export const welcome = ({ slot, id, time, cats }) => ({ t: "welcome", proto: PROTO_VERSION, win: { min: PROTO_MIN, max: PROTO_VERSION },
    slot, id, time, cats });
/** A cat arrived, or renamed itself, or was rebuilt in the customizer.  One
    message for all three: they all mean "this slot's record has changed", and
    a client that handles it by replacing the record is correct for each. */
export const cat = (slot, rec) => ({ t: "cat", slot, rec });
export const gone = (slot) => ({ t: "gone", slot });
export const snap = (time, rows) => ({ t: "snap", time: round2(time), c: rows });
/** You own this cat now.  The token is the only secret the system has, and it
    is sent exactly once, to exactly one socket. */
export const claim = (slot, id, token) => ({ t: "claim", slot, id, token });
/** `window` rides only on DENY.VERSION (as flat `min`/`max` -- §4.3's "the
    deny payload"), where the actable advice depends on which side of the
    window the client fell off.  Optional so every existing call site -- and
    every existing deny on the wire -- is byte-identical to before. */
export const deny = (why, window, open) => {
    const m = window
        ? { t: "deny", why, min: window.min, max: window.max }
        : { t: "deny", why };
    // `open` rides only on DENY.FULL and only when TRUE: with reclaim off the
    // FULL deny is byte-identical to every one already on the wire.
    if (open === true)
        m.open = true;
    return m;
};
/** Your own cat's wants, in PLACES order, plus how far into a visit it is,
    plus the hunt tally (the collection readout is the owner's business, same
    as the wants -- see wireRecord).  `tal` is omitted from the JSON when the
    caller has none to send, which is also what a pre-tally server looks like
    to a newer client: the HUD treats "no tal" as "nothing to show". */
export const needs = (wants, progress, tally) => ({ t: "needs", w: wants.map(round2), p: progress, tal: tally });
/** Somebody's cat just banked a happy place.  Everyone sees it: a street where
    other people's cats are visibly getting somewhere is the point of putting
    sixteen of them in one world. */
export const bliss = (slot, place) => ({ t: "bliss", slot, place });
export const pong = (c) => ({ t: "pong", c });
/** The prey, one row each, at the snapshot cadence.  Sent whole every time
    rather than as deltas: seven-ish rows of eight numbers is ~300 bytes, a
    late joiner needs the full set anyway, and delta bookkeeping for 6 KB/s
    per client would be complexity spent on the cheapest message we send. */
export const preySnap = (rows) => ({ t: "prey", p: rows });
/** A hunt moment everyone should see.  kind is "caught" (a capture, out in
    the world) or "home" (the catch delivered inside the house, the double).
    Broadcast for the same reason bliss is: a street where somebody's cat is
    visibly trotting home with a mouse is the point of sharing the street. */
export const hunt = (slot, kind, species) => ({ t: "hunt", slot, kind, species });
/** Somebody's cat spoke a line from the phrase book.  An object, not a
    tuple: a chat message is a handful a minute across the whole street, so
    it lands on the objects-for-everything-else side of rule 1.  And `p` is
    an index, never text -- the same index the client sent, rebroadcast with
    the server-known slot attached, so the server never handles a string of
    chat in its life. */
export const say = (slot, p) => ({ t: "say", slot, p });
/** Your own cat's treasure state: one FIND_STATE (constants.js) per
    shared/happy.js FINDS row, in FINDS order.  Owner-only, riding the same
    cadence as `needs`, because it is the same kind of message: the private
    readout of a record the server holds.  Seven small ints is nothing on
    the wire, and sending the whole vector every time means a late joiner,
    a reconnect and a re-hide expiry all need no special case.

    There is deliberately NO client->server message for a find anywhere in
    this file, for exactly the prey rows' reason: the server judges a find
    from the pose it already accepted and the table it already holds, so a
    client that could say "I found it" would be a client that can mint
    happiness -- rule 2 at the top of this file. */
export const finds = (state) => ({ t: "finds", f: state });
/** Somebody's cat dug out a treasure.  `id` is a FINDS row id (a string,
    like bliss's place id -- this is a rare, readable message, rule 1's
    object side).  `first` says whether it was a DISCOVERY (first time ever
    for that cat) or a re-find of something already in the collection.

    Discoveries are broadcast street-wide like a bliss: the toast names the
    treasure but never says where it was, so a sibling hearing "Milo found
    the jingly ball" gets the fun of knowing it exists -- and a nudge that
    the street holds secrets -- without the finding being done for them.
    Re-finds go to the owner alone; the street does not need to hear about
    the same coin twice. */
export const found = (slot, id, first) => ({ t: "found", slot, id, first });
/**
 * What time of day it is, as the server's word.  `ph` is the phase from
 * shared/constants.js's dayPhase: [0, 1), midnight to midnight.
 *
 * Broadcast about once a second (world.js owns the cadence) rather than in
 * every snapshot: the sky moves at 1/DAY_LENGTH_S per second, so a 20 Hz
 * copy would be nineteen unread repeats -- the client integrates the phase
 * itself between words and only eases out the residual error.  Rounded to
 * 1e-5 of a day, which is 12 ms of a 20-minute cycle -- far below anything
 * a sun's position can show -- because round3 would quantise the word to
 * 1.2 s and make every correction a visible little shove at dusk.
 *
 * This message did NOT bump PROTO_VERSION, and the argument is `say`'s: a
 * client one deploy behind drops an unknown tag on the floor (client.js's
 * _emit ignores tags nobody subscribed to), so the skew degrades to that
 * tab keeping its fixed late-afternoon sky until it reloads -- shabby, not
 * broken, and nothing else about the wire changed shape.  Bumping would
 * kick all sixteen mid-session tabs to fix a sky.
 */
export const day = (ph) => ({ t: "day", ph: Math.round(ph * 1e5) / 1e5 });
// ---------------------------------------------------------------------------
// The dog (server/src/dog.js).  Import beside its constructors rather than
// in the header block: imports hoist, so this is purely a layout choice --
// the dog's whole wire presence stays in one appendable block, which is the
// same bargain the append-only mode array itself strikes.
// ---------------------------------------------------------------------------
import { DOG_MODES } from "./constants.js";
/**
 * The dog's row.  A tuple, same reasoning as the cat and prey rows: it
 * rides every snapshot.  MODE indexes DOG_MODES (append-only), TARGET is
 * the cat slot being chased -- or, during a woof, the one just tagged --
 * or -1, which is how a client aims the theatre at somebody without a
 * second message.
 *
 * There is deliberately NO client->server message for the dog, and no
 * parseClientMessage case: the chase, the tag and the fright are all
 * decided server-side from poses the server already accepted.  A client
 * that could say anything at all about the dog could say "it didn't get
 * me", and rule 2 at the top of this file is why it cannot.
 */
export const DogCol = { MODE: 0, X: 1, Y: 2, Z: 3, HEADING: 4, TARGET: 5 };
export const DOG_ROW_LEN = 6;
export function dogRow({ mode, x, y, z, heading, target }) {
    const mi = DOG_MODES.indexOf(mode);
    return [
        mi < 0 ? 0 : mi,
        round2(x), round2(y), round2(z),
        round3(heading),
        Number.isInteger(target) ? target : -1,
    ];
}
/** The dog, at the snapshot cadence beside `snap` and `prey`.  Sent whole
    every time for the prey message's reason, only more so: one row of six
    numbers is the cheapest message the server sends. */
export const dogSnap = (row) => ({ t: "dog", d: row });
/** Somebody's cat blundered into a burr bush and the sting landed.  Server
    to client only, like everything the burrs say: there is deliberately NO
    client->server message anywhere in the mechanic -- a snag is judged from
    the pose the server already accepted against bush positions the level
    build fixed, so a client that could say "I am burred" (or "I am not")
    would be a client that can mint or dodge a penalty, and rule 2 at the
    top of this file is the whole reason it cannot.  Broadcast rather than
    sent to the owner alone for bliss's reason inverted: a street where a
    cat can be seen paying for a blunder is a street where the bushes are
    believed.  The owner's own client uses it to pin the "that cost you"
    cue to the exact tick the meter dips (game/src/burrs.js). */
export const burr = (slot) => ({ t: "burr", slot });
